import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, AlertCircle, Eye, XCircle } from 'lucide-react';
import ReservationOpsModal from '../../components/ReservationOpsModal';
import { AdminPageHeader, AdminPageShell, AdminPageBody } from '../../components/AdminPageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const formatDT = (v) => v ? new Date(v).toLocaleString('pt-BR') : '-';
const formatCpf = (c) => {
  const d = (c || '').replace(/\D/g, '').padStart(11, '0').slice(-11);
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`;
};

const AdminCancelRequests = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [opsModal, setOpsModal] = useState({ open: false, mode: null, reservation: null });
  const [analyzePreview, setAnalyzePreview] = useState({ open: false, data: null, loading: false, error: null, reservation: null });

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) { navigate('/admin/login'); return; }
    load();
  }, [navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/reservations-ops/pending-cancel-requests`);
      setItems(res.data.items || []);
    } catch (e) {
      setMsg({ type: 'error', text: 'Erro ao carregar solicitações.' });
    } finally { setLoading(false); }
  };

  const openAnalyze = async (res) => {
    setAnalyzePreview({ open: true, data: null, loading: true, error: null, reservation: res });
    try {
      const r = await axios.post(`${BACKEND_URL}/api/cancellations/preview`, {
        reservation_id: res.id, by_agency: false,
      });
      setAnalyzePreview(s => ({ ...s, data: r.data, loading: false }));
    } catch (e) {
      setAnalyzePreview(s => ({ ...s, error: e.response?.data?.detail || 'Erro no preview.', loading: false }));
    }
  };

  const denyRequest = async (res) => {
    if (!window.confirm(`Negar a solicitação de cancelamento de ${res.customer_name}?`)) return;
    try {
      // Reverte status e apaga cancellation_request
      await axios.patch(`${BACKEND_URL}/api/reservations/${res.id}`, {
        status: res.previous_status || 'confirmed',
        cancellation_request: null,
      }).catch(() => {});
      // Fallback: usa endpoint deny (criaremos)
      await axios.post(`${BACKEND_URL}/api/reservations-ops/deny-cancel-request`, {
        reservation_id: res.id,
      }).catch(() => {});
      setMsg({ type: 'success', text: 'Solicitação negada.' });
      load();
    } catch (e) {
      setMsg({ type: 'error', text: 'Erro ao negar.' });
    }
  };

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Solicitações de Cancelamento"
        description="Fila de reservas com solicitação registrada pelo cliente"
        Icon={AlertCircle}
        iconClassName="text-orange-600"
      />

      <AdminPageBody>
        {msg && (
          <div className={`p-3 rounded ${msg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{msg.text}</div>
        )}

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">Aguardando análise ({items.length})</h3>
              <Button variant="outline" size="sm" onClick={load}>Atualizar</Button>
            </div>
            {loading ? <p className="text-sm text-gray-500">Carregando…</p> : items.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8" data-testid="empty-requests">Nenhuma solicitação pendente.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">Cliente</th>
                      <th className="p-2 text-left">CPF</th>
                      <th className="p-2 text-left">Reserva / Pacote</th>
                      <th className="p-2 text-left">Data da Viagem</th>
                      <th className="p-2 text-right">Valor Pago</th>
                      <th className="p-2 text-left">Solicitado em</th>
                      <th className="p-2 text-center">Status</th>
                      <th className="p-2 text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(it => (
                      <tr key={it.id} className="border-b hover:bg-gray-50" data-testid={`request-row-${it.id}`}>
                        <td className="p-2 font-medium">{it.customer_name}</td>
                        <td className="p-2 font-mono text-xs">{formatCpf(it.customer_cpf)}</td>
                        <td className="p-2">
                          <div>{it.package_name}</div>
                          <div className="text-xs text-gray-500">#{it.id?.slice(0,8)}</div>
                        </td>
                        <td className="p-2">{it.selected_date}</td>
                        <td className="p-2 text-right font-medium">{formatBRL(it.final_total || it.total_amount)}</td>
                        <td className="p-2 text-xs">{formatDT(it.cancellation_request?.requested_at)}</td>
                        <td className="p-2 text-center">
                          <Badge className="bg-orange-100 text-orange-800">Aguardando análise</Badge>
                        </td>
                        <td className="p-2 text-center">
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant="outline" onClick={() => openAnalyze(it)} data-testid={`analyze-${it.id}`}>
                              <Eye className="w-3 h-3 mr-1" /> Analisar
                            </Button>
                            <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setOpsModal({ open: true, mode: 'cancel', reservation: it })} data-testid={`execute-${it.id}`}>
                              Executar
                            </Button>
                            <Button size="sm" variant="outline" className="border-gray-400" onClick={() => denyRequest(it)} data-testid={`deny-${it.id}`}>
                              <XCircle className="w-3 h-3 mr-1" /> Negar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

      {/* Analisar preview */}
      {analyzePreview.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAnalyzePreview({ open: false, data: null, loading: false, error: null, reservation: null })}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-2">Cálculo do cancelamento</h3>
            {analyzePreview.loading && <p>Calculando…</p>}
            {analyzePreview.error && <p className="text-red-600">{analyzePreview.error}</p>}
            {analyzePreview.data && (
              <div className="text-sm space-y-1" data-testid="analyze-result">
                <p><strong>Regra:</strong> {analyzePreview.data.rule_applied}</p>
                <p><strong>Valor pago:</strong> {formatBRL(analyzePreview.data.paid_amount)}</p>
                <p><strong>Custos não recuperáveis:</strong> {formatBRL(analyzePreview.data.non_recoverable_costs)}</p>
                <p><strong>Taxa administrativa:</strong> {formatBRL(analyzePreview.data.administrative_fee)}</p>
                <p><strong>Saldo remanescente:</strong> {formatBRL(analyzePreview.data.remaining_balance)}</p>
                <p><strong>Prazo até viagem:</strong> {analyzePreview.data.days_until_trip} dia(s)</p>
                <p><strong>Retenção:</strong> {analyzePreview.data.retention_percentage}% = {formatBRL(analyzePreview.data.retention_value)}</p>
                <p className="text-lg font-bold text-emerald-800 pt-2 border-t mt-2">Elegível: {formatBRL(analyzePreview.data.refund_eligible)}</p>
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => setAnalyzePreview({ open: false, data: null, loading: false, error: null, reservation: null })}>Fechar</Button>
              {analyzePreview.data && analyzePreview.reservation && (
                <Button className="flex-1 bg-red-600" onClick={() => {
                  setOpsModal({ open: true, mode: 'cancel', reservation: analyzePreview.reservation });
                  setAnalyzePreview({ open: false, data: null, loading: false, error: null, reservation: null });
                }}>Ir para Executar</Button>
              )}
            </div>
          </div>
        </div>
      )}

      <ReservationOpsModal
        open={opsModal.open}
        mode={opsModal.mode}
        reservation={opsModal.reservation}
        onOpenChange={(v) => setOpsModal(s => ({ ...s, open: v }))}
        onDone={load}
      />
      </AdminPageBody>
    </AdminPageShell>
  );
};

export default AdminCancelRequests;
